/**
 * Price formatting utility functions
 * Standardized to 3 significant digits with SH notation
 * @module priceFormatter
 */

/**
 * Format number to 3 significant digits
 * @param {number} value - Number to format
 * @returns {string} - Formatted string
 */
export const formatToSignificantDigits = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00';
  }

  const num = parseFloat(value);

  if (num === 0) {
    return '0.00';
  }

  // Display with 3 significant digits (always keep 3 digits)
  if (num >= 1000) {
    // For values 1000+ display with K/M units
    if (num >= 1000000) {
      return (num / 1000000).toPrecision(3) + 'M';
    } else {
      return (num / 1000).toPrecision(3) + 'K';
    }
  } else {
    // For values under 1000, display normally (3 significant digits)
    return num.toPrecision(3);
  }
};

/**
 * Format SH token price
 * @param {number} price - Price in SH units
 * @returns {string} - Formatted price string (e.g. "100.4 SH", "10.50 KSH")
 */
export const formatSHPrice = (price) => {
  const formattedValue = formatToSignificantDigits(price);

  // Convert K/M units to KSH/MSH when included
  if (formattedValue.includes('K')) {
    return formattedValue.replace('K', ' KSH');
  } else if (formattedValue.includes('M')) {
    return formattedValue.replace('M', ' MSH');
  } else {
    return `${formattedValue} SH`;
  }
};

/**
 * Format user balance for display (token type compatible)
 * @param {number} balance - Balance amount
 * @param {string} tokenSymbol - Token symbol (default: SH)
 * @returns {string} - Formatted balance string
 */
export const formatTokenBalance = (balance, tokenSymbol = 'SH') => {
  const formattedValue = formatToSignificantDigits(balance);

  // Process for non-base currency (other than SHINKETSU)
  if (tokenSymbol === 'SHINKETSU') {
    return formatSHPrice(balance);
  }

  // For other tokens
  if (formattedValue.includes('K')) {
    return `${formattedValue.replace('K', '')} K${tokenSymbol}`;
  } else if (formattedValue.includes('M')) {
    return `${formattedValue.replace('M', '')} M${tokenSymbol}`;
  } else {
    return `${formattedValue} ${tokenSymbol}`;
  }
};

/**
 * Price comparison display (value assessment)
 * @param {number} currentPrice - Current price
 * @param {number} comparePrice - Comparison price
 * @returns {object} - {formattedCurrent, formattedCompare, percentage, isHigher}
 */
export const comparePrices = (currentPrice, comparePrice) => {
  const formattedCurrent = formatSHPrice(currentPrice);
  const formattedCompare = formatSHPrice(comparePrice);

  if (comparePrice === 0) {
    return {
      formattedCurrent,
      formattedCompare,
      percentage: null,
      isHigher: null,
    };
  }

  const percentage = (
    ((currentPrice - comparePrice) / comparePrice) *
    100
  ).toFixed(1);
  const isHigher = currentPrice > comparePrice;

  return {
    formattedCurrent,
    formattedCompare,
    percentage: `${isHigher ? '+' : ''}${percentage}%`,
    isHigher,
  };
};

/**
 * Validate numeric input
 * @param {string} input - Input string
 * @returns {object} - {isValid, value, error}
 */
export const validatePriceInput = (input) => {
  if (!input || input.trim() === '') {
    return { isValid: false, value: null, error: 'Please enter a price' };
  }

  const value = parseFloat(input);

  if (isNaN(value)) {
    return {
      isValid: false,
      value: null,
      error: 'Please enter a valid number',
    };
  }

  if (value < 0) {
    return {
      isValid: false,
      value: null,
      error: 'Price must be 0 or greater',
    };
  }

  if (value > 999999999) {
    return { isValid: false, value: null, error: 'Price is too large' };
  }

  return { isValid: true, value, error: null };
};

/**
 * Format reward display (for recruitment details)
 * @param {string} rewardType - Reward type (token, fixed, profit_share, equity, other)
 * @param {number} rewardAmount - Reward amount
 * @param {number} rewardPercentage - Reward percentage
 * @param {string} rewardDescription - Reward description
 * @returns {string} - Formatted reward string
 */
export const formatReward = (
  rewardType,
  rewardAmount,
  rewardPercentage,
  rewardDescription
) => {
  if (!rewardType) return 'Not set';

  switch (rewardType) {
    case 'token':
      if (rewardAmount) {
        return `${formatSHPrice(rewardAmount)} /month`;
      }
      return 'Token reward';

    case 'fixed':
      if (rewardAmount) {
        return `${formatSHPrice(rewardAmount)} /month`;
      }
      return 'Fixed reward';

    case 'profit_share':
      if (rewardPercentage) {
        return `${rewardPercentage}% of revenue`;
      }
      return 'Revenue share';

    case 'equity':
      if (rewardPercentage) {
        return `${rewardPercentage}% equity`;
      }
      return 'Equity reward';

    case 'other':
      return rewardDescription || 'Other reward';

    default:
      return rewardType;
  }
};

/**
 * Format hourly/daily rate display
 * @param {number} amount - Amount
 * @param {string} unit - Unit (hour, day, task)
 * @returns {string} - Formatted string
 */
export const formatHourlyRate = (amount, unit = 'hour') => {
  const formattedAmount = formatSHPrice(amount);

  switch (unit) {
    case 'hour':
      return `${formattedAmount} /hour`;
    case 'day':
      return `${formattedAmount} /day`;
    case 'task':
      return `${formattedAmount} /task`;
    default:
      return formattedAmount;
  }
};
// Vercel sync comment - Mon Oct 27 17:57:45 JST 2025
// Vercel sync - Tue Oct 28 02:13:09 JST 2025
// Vercel forced recognition - Tue Oct 28 08:45:00 JST 2025 - File existence force sync
// ASCII compatibility fix - Oct 28 2025 - Non-ASCII character removal for Vercel Linux environment
